/**
 * `/audits/:id` — the audit workspace (`AuditWorkspace.dc.html`).
 *
 * §11.5: an auditor spends hours here, so this is the screen that decides adoption.
 * What is implemented from that section: the three panes, autosave with a visible
 * saved/failed indicator and no Save button, the section rail with live progress, the
 * drawer rendering from the payload already fetched, section as URL state so a filtered
 * view can be pasted into a chat, and the `j`/`k`/`1`–`6` keyboard model with its hint
 * bar — bound to the list container rather than `document`, inert inside text fields,
 * and never intercepting a modifier combination.
 *
 * **Not yet: virtualisation.** A 218-item section renders every row. That is a §11.5
 * requirement and it is listed at handover rather than quietly skipped; the largest
 * single section is 31 items, so it is a real gap that no current audit reaches.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type { AuditWorkspace, EvidenceSystem } from '@portal/contracts';
import type { ItemState } from '@portal/domain';
import {
  Avatar,
  EvidenceGlyph,
  ItemRow,
  MarkerChip,
  PermissionButton,
  StateChip,
  WarningGlyph,
  stateLabel,
} from '@portal/ui';

import { ApiClientError, api } from '../api.js';
import type { ReportAudience } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const STATES: ItemState[] = ['PENDING', 'PARTIAL', 'VERIFIED', 'REJECTED', 'NOT_APPLICABLE', 'WAIVED'];

type Row = AuditWorkspace['items'][number];
type RepoRow = Row['repoResults'][number];

/** Identifies one cell of the repository matrix across item changes. */
const repoKey = (ref: string, repositoryId: string): string => `${ref}::${repositoryId}`;

type SaveState = { ref: string; status: 'saving' | 'saved' | 'failed'; message?: string } | null;

export function AuditWorkspaceRoute() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const section = params.get('section') ?? 'A';
  const focusedRef = params.get('item');

  const [save, setSave] = useState<SaveState>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState('');
  const [draftAttestation, setDraftAttestation] = useState('');
  const [evidenceLabel, setEvidenceLabel] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [listFocused, setListFocused] = useState(false);
  /**
   * Per-repository gap notes being typed, keyed by `ref::repositoryId` because the panel
   * moves between items.
   *
   * A key being present means "Partial has been chosen for this repository and is waiting
   * for its note". The write is held until there is one: the API refuses PARTIAL without
   * a note, and sending it first would put an error on screen that cannot be acted on
   * from where the person is standing.
   */
  const [repoGapDraft, setRepoGapDraft] = useState<Record<string, string>>({});

  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement | null>(null);
  const evidenceUrlRef = useRef<HTMLInputElement | null>(null);
  /** Per-row nodes, so `j`/`k` can scroll the focused row into view. */
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const workspace = useQuery({
    queryKey: ['audit', id, section],
    queryFn: () => api.auditWorkspace(id, section),
    enabled: id !== '',
  });

  const focused: Row | undefined =
    workspace.data?.items.find((item) => item.ref === focusedRef) ?? workspace.data?.items[0];

  useEffect(() => {
    setDraftNote(focused?.gapNote ?? '');
    setDraftAttestation(focused?.attestation ?? '');
  }, [focused?.ref, focused?.gapNote, focused?.attestation]);

  /** Autosave: one write per change, with the outcome shown next to the item (§11.5). */
  const patch = useMutation({
    mutationFn: (input: Parameters<typeof api.patchItem>[2] & { ref: string }) =>
      api.patchItem(id, input.ref, input),
    onMutate: (input) => setSave({ ref: input.ref, status: 'saving' }),
    onSuccess: async (_result, input) => {
      setSave({ ref: input.ref, status: 'saved' });
      await queryClient.invalidateQueries({ queryKey: ['audit', id] });
    },
    onError: (error, input) =>
      setSave({
        ref: input.ref,
        status: 'failed',
        message: error instanceof ApiClientError ? error.message : 'The change was not saved.',
      }),
  });

  const patchRepo = useMutation({
    // `gapNote` is not optional decoration: the API refuses PARTIAL on a repository
    // without one, and this mutation used to have no way to send it — so Partial was
    // unreachable in the matrix however carefully the note above was filled in.
    mutationFn: (input: {
      ref: string;
      repositoryId: string;
      state: ItemState;
      gapNote?: string;
    }) =>
      api.patchRepoItem(id, input.ref, input.repositoryId, {
        version: 0,
        state: input.state,
        ...(input.gapNote === undefined ? {} : { gapNote: input.gapNote }),
      }),
    onMutate: (input) => setSave({ ref: input.ref, status: 'saving' }),
    onSuccess: async (_r, input) => {
      setSave({ ref: input.ref, status: 'saved' });
      await queryClient.invalidateQueries({ queryKey: ['audit', id] });
    },
    onError: (error, input) =>
      setSave({
        ref: input.ref,
        status: 'failed',
        message: error instanceof ApiClientError ? error.message : 'The change was not saved.',
      }),
  });

  /**
   * Saves a per-repository gap note, and with it the Partial that was waiting on it.
   *
   * Called on blur and on Enter. A still-blank note is left held rather than sent: the
   * repository keeps whatever state it had, and the field stays open.
   */
  function commitRepoNote(ref: string, repo: RepoRow): void {
    const key = repoKey(ref, repo.repositoryId);
    const draft = repoGapDraft[key];
    if (draft === undefined) return;

    const drop = () =>
      setRepoGapDraft((drafts) => {
        const { [key]: _done, ...rest } = drafts;
        return rest;
      });

    const note = draft.trim();
    if (note === '') {
      // Emptied. If there is a stored note, put it back on screen rather than leaving a
      // blank field that looks stuck; if there is not, keep holding — Partial needs one.
      if ((repo.gapNote ?? '').trim() !== '') drop();
      return;
    }

    drop();

    // Nothing actually changed — do not spend a write or a version bump on it.
    if (note === (repo.gapNote ?? '').trim() && repo.state === 'PARTIAL') return;

    patchRepo.mutate({ ref, repositoryId: repo.repositoryId, state: 'PARTIAL', gapNote: note });
  }

  const addEvidence = useMutation({
    mutationFn: (input: { ref: string; label: string; url: string; system: EvidenceSystem }) =>
      api.addEvidence(id, input.ref, {
        kind: 'LINK',
        system: input.system,
        url: input.url,
        label: input.label,
      }),
    onSuccess: async () => {
      setEvidenceLabel('');
      setEvidenceUrl('');
      await queryClient.invalidateQueries({ queryKey: ['audit', id] });
    },
  });

  if (workspace.isPending) {
    return <div style={{ padding: '24px 28px', color: 'var(--mu)', fontSize: 13 }}>Loading the audit…</div>;
  }

  if (workspace.isError || workspace.data === undefined) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 620 }}>
        <h1 style={{ margin: '0 0 8px', font: '600 19px/1.2 var(--font-sans)' }}>
          This audit could not be loaded
        </h1>
        <p style={{ fontSize: 12.5, color: 'var(--mu)' }}>
          {workspace.error instanceof ApiClientError ? workspace.error.message : 'Something went wrong.'}
        </p>
      </div>
    );
  }

  const data = workspace.data;
  const readOnly = data.audit.readOnly;

  const saveReport = (audience: ReportAudience): void => {
    setReportError(null);
    api
      .downloadAuditReport(id, audience)
      .then(({ filename, blob }) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((error: unknown) => {
        setReportError(error instanceof ApiClientError ? error.message : 'The report could not be downloaded.');
      });
  };

  const setState = (row: Row, state: ItemState): void => {
    patch.mutate({
      ref: row.ref,
      version: row.version,
      state,
      ...(state === 'PARTIAL' ? { gapNote: draftNote } : {}),
      ...(state === 'VERIFIED' && draftAttestation.trim() !== ''
        ? { attestation: draftAttestation }
        : {}),
    });
  };

  /** Move the focused item by `delta` rows, clamped to the ends of the current section. */
  const moveFocus = (delta: number): void => {
    if (focused === undefined) return;
    const index = data.items.findIndex((item) => item.ref === focused.ref);
    const next = data.items[Math.min(Math.max(index + delta, 0), data.items.length - 1)];
    if (next === undefined || next.ref === focused.ref) return;

    const updated = new URLSearchParams(params);
    updated.set('item', next.ref);
    setParams(updated, { replace: true });
    // Keep the newly focused row on screen without stealing focus from the container.
    rowRefs.current[next.ref]?.scrollIntoView({ block: 'nearest' });
  };

  /**
   * §11.5 — the keyboard model. Bound to the list container, not `document`, so it only
   * fires when the list has focus and never competes with the drawer's inputs.
   *
   * Two rules that matter more than the shortcuts themselves: a modifier combination is
   * always left alone (browser undo, copy, find must keep working), and a keystroke aimed
   * at a text field is never intercepted — otherwise typing "1" into a gap note would
   * silently set a state.
   */
  const onListKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const target = event.target as HTMLElement;
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
      return;
    }

    const key = event.key;

    if (key === 'j' || key === 'ArrowDown') {
      event.preventDefault();
      moveFocus(1);
      return;
    }
    if (key === 'k' || key === 'ArrowUp') {
      event.preventDefault();
      moveFocus(-1);
      return;
    }
    if (key === '?') {
      event.preventDefault();
      setShowShortcuts((on) => !on);
      return;
    }
    if (key === 'Escape') {
      setShowShortcuts(false);
      return;
    }
    if (key === 'r') {
      event.preventDefault();
      void navigate(`/audits/${id}/matrix`);
      return;
    }
    if (key === 'e') {
      event.preventDefault();
      evidenceUrlRef.current?.focus();
      return;
    }

    // 1–6 set the state of the focused item, in the same order as the picker on screen.
    const digit = Number.parseInt(key, 10);
    if (!Number.isNaN(digit) && digit >= 1 && digit <= STATES.length) {
      event.preventDefault();
      if (readOnly || focused === undefined) return;
      const state = STATES[digit - 1];
      if (state !== undefined) setState(focused, state);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 56px)',
        minHeight: 0,
        // The shortcuts overlay positions against this.
        position: 'relative',
      }}
    >
      {/* Section rail, with live progress (§11.5: progress is always visible). */}
      <aside
        style={{
          width: 256,
          flex: '0 0 256px',
          borderRight: '1px solid var(--bd)',
          background: 'var(--sf)',
          overflowY: 'auto',
          padding: '14px 0',
        }}
      >
        <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
              Audit #{data.audit.number}
            </span>
            <span
              style={{
                ...LABEL,
                background: readOnly ? 'var(--sunk)' : 'var(--acs)',
                color: readOnly ? 'var(--mu)' : 'var(--ac)',
                padding: '2px 6px',
                borderRadius: 'var(--r-md)',
              }}
            >
              {data.audit.status.replace('_', ' ')}
            </span>
          </div>
          <Link to={`/projects/${data.audit.projectId}`} style={{ fontSize: 12 }}>
            {data.audit.projectName}
          </Link>
          <span style={{ fontSize: 11.5, color: 'var(--mu)' }}>
            {data.audit.typeName} · {data.audit.periodLabel} · checklist{' '}
            <span className="mono">v{data.audit.templateVersion}</span>
          </span>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
            <StateChip state="VERIFIED" size="sm" /> <span className="mono" style={{ fontSize: 11 }}>{data.progress.verified}</span>
            <StateChip state="PARTIAL" size="sm" /> <span className="mono" style={{ fontSize: 11 }}>{data.progress.partial}</span>
            <StateChip state="REJECTED" size="sm" /> <span className="mono" style={{ fontSize: 11 }}>{data.progress.rejected}</span>
            <StateChip state="PENDING" size="sm" /> <span className="mono" style={{ fontSize: 11 }}>{data.progress.pending}</span>
          </div>
        </div>

        <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ ...LABEL, padding: '0 6px 6px' }}>Sections</span>
          {data.sections.map((entry) => (
            <button
              key={entry.section}
              onClick={() => setParams({ section: entry.section })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '6px 10px',
                borderRadius: 'var(--r-md)',
                border: 'none',
                background: entry.section === section ? 'var(--acs)' : 'transparent',
                color: 'var(--tx)',
                font: `${entry.section === section ? 600 : 400} 12.5px/1.3 var(--font-sans)`,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span className="mono" style={{ width: 12 }}>{entry.section}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {entry.title}
              </span>
              {entry.hasRejected && (
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ba)' }} />
              )}
              <span className="mono" style={{ fontSize: 11, color: 'var(--mu)' }}>
                {entry.resolved}/{entry.inScope}
              </span>
            </button>
          ))}
        </div>

        <div style={{ padding: '14px 14px 0', marginTop: 10, borderTop: '1px solid var(--bd)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Link to={`/audits/${id}/evidence`} style={{ fontSize: 12 }}>Evidence catalog →</Link>
          <Link to={`/audits/${id}/review`} style={{ fontSize: 12 }}>Review &amp; submit →</Link>
          {data.audit.status === 'CLOSED' && (
            <>
              <PermissionButton onClick={() => saveReport('internal')}>Download report (.md)</PermissionButton>
              <PermissionButton onClick={() => saveReport('customer')}>Customer version (.md)</PermissionButton>
              {reportError !== null && (
                <span role="alert" style={{ fontSize: 11, color: 'var(--rd, #b42318)' }}>{reportError}</span>
              )}
            </>
          )}
        </div>
      </aside>

      {/* Item list */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
        {readOnly && (
          <div
            style={{
              display: 'flex',
              gap: 9,
              alignItems: 'center',
              padding: '9px 14px',
              background: 'var(--sunk)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 11.5,
              color: 'var(--mu)',
            }}
          >
            <WarningGlyph size={13} />
            {data.audit.readOnlyReason}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 14px',
            background: 'var(--sunk)',
            borderBottom: '1px solid var(--bd)',
          }}
        >
          <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
            Section {section}
          </span>
          <span style={{ fontSize: 11, color: 'var(--mu)' }}>{data.items.length} items</span>
          {save !== null && (
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 11,
                fontWeight: 600,
                color:
                  save.status === 'failed'
                    ? 'var(--ba)'
                    : save.status === 'saved'
                      ? 'var(--ok)'
                      : 'var(--mu)',
              }}
            >
              {save.status === 'saving'
                ? `saving ${save.ref}…`
                : save.status === 'saved'
                  ? `${save.ref} saved`
                  : `${save.ref} not saved`}
            </span>
          )}
        </div>

        {/*
          The keyboard surface. `tabIndex` makes it focusable so the handler has somewhere
          to live other than `document`; the outline is suppressed because the focused
          ITEM is the visible affordance, not the container.
        */}
        <div
          ref={listRef}
          tabIndex={0}
          role="listbox"
          aria-label={`Section ${section} items`}
          onKeyDown={onListKeyDown}
          onFocus={() => {
            setListFocused(true);
          }}
          onBlur={(event) => {
            // Focus moving to a row inside the list still counts as the list having it.
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setListFocused(false);
            }
          }}
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', outline: 'none' }}
        >
          {data.items.map((row) => (
            <div
              key={row.ref}
              ref={(node) => {
                rowRefs.current[row.ref] = node;
              }}
            >
            <ItemRow
              ref_={row.ref}
              obligation={row.obligation}
              isContextual={row.isContextual}
              statement={row.statement}
              state={row.state}
              provenance={[
                ...(row.origin === 'INHERITED' && row.inheritedFromAuditNumber !== null
                  ? [{ kind: 'inherited' as const, auditNumber: row.inheritedFromAuditNumber }]
                  : []),
                ...(row.attested ? [{ kind: 'attested' as const, by: row.owner?.displayName ?? 'the auditor' }] : []),
                ...(row.freshness?.status === 'EXPIRED' ? [{ kind: 'expired' as const }] : []),
                ...(row.freshness?.status === 'EXPIRING_SOON'
                  ? [{ kind: 'expiring' as const, daysRemaining: row.freshness.daysRemaining }]
                  : []),
              ]}
              rollUp={row.rollUp}
              evidenceCount={row.evidenceCount}
              owner={row.owner}
              selected={focused?.ref === row.ref}
              onSelect={() => {
                setParams({ section, item: row.ref });
                listRef.current?.focus();
              }}
            />
            </div>
          ))}
        </div>

        {/* The hint bar the artboard specifies — discoverability, not decoration. */}
        <div
          style={{
            flex: '0 0 auto',
            borderTop: '1px solid var(--bd)',
            background: 'var(--sf)',
            padding: '6px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          {[
            ['j / k', 'move'],
            ['1–6', 'set state'],
            ['e', 'evidence'],
            ['r', 'repositories'],
            ['?', 'shortcuts'],
          ].map(([keys, what]) => (
            <span key={keys} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <kbd
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--r-sm)',
                  padding: '2px 5px',
                  color: 'var(--mu)',
                  background: 'var(--bg)',
                }}
              >
                {keys}
              </kbd>
              <span style={{ fontSize: 10.5, color: 'var(--mu)' }}>{what}</span>
            </span>
          ))}
          <span style={{ fontSize: 10.5, color: 'var(--mu)', marginLeft: 'auto' }}>
            {listFocused ? 'list has focus' : 'click the list to use shortcuts'}
          </span>
        </div>
      </div>

      {/* ? — the full list, over the list rather than in a modal that traps focus. */}
      {showShortcuts && (
        <div
          style={{
            position: 'absolute',
            right: 'calc(var(--w-drawer) + 24px)',
            bottom: 48,
            width: 320,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--r-lg)',
            boxShadow: '0 8px 24px rgba(17,24,39,.14)',
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            zIndex: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 12.5 }}>Shortcuts</strong>
            <span style={{ fontSize: 10.5, color: 'var(--mu)', marginLeft: 'auto' }}>
              ? or Esc to close
            </span>
          </div>
          {[
            ['j / ↓', 'next item'],
            ['k / ↑', 'previous item'],
            ['1', 'Pending'],
            ['2', 'Partial'],
            ['3', 'Verified'],
            ['4', 'Rejected'],
            ['5', 'Not applicable'],
            ['6', 'Waived'],
            ['e', 'jump to the evidence field'],
            ['r', 'repository matrix'],
          ].map(([keys, what]) => (
            <div key={keys} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <kbd
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--r-sm)',
                  padding: '2px 6px',
                  minWidth: 42,
                  textAlign: 'center',
                  color: 'var(--tx)',
                  background: 'var(--bg)',
                }}
              >
                {keys}
              </kbd>
              <span style={{ fontSize: 11.5 }}>{what}</span>
            </div>
          ))}
          <span
            style={{
              fontSize: 10.5,
              color: 'var(--mu)',
              lineHeight: 1.5,
              borderTop: '1px solid var(--bd-soft)',
              paddingTop: 8,
            }}
          >
            Shortcuts are inert while a text field has focus, and modifier combinations are never
            intercepted — copy, paste and undo keep working.
          </span>
        </div>
      )}

      {/* Drawer — rendered from the payload already fetched (§7.3), so it opens instantly. */}
      {focused !== undefined && (
        <aside
          style={{
            width: 'var(--w-drawer)',
            flex: '0 0 var(--w-drawer)',
            borderLeft: '1px solid var(--bd)',
            background: 'var(--sf)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{focused.ref}</span>
            <MarkerChip obligation={focused.obligation} isContextual={focused.isContextual} />
            <span style={{ ...LABEL }}>{focused.subsection}</span>
            <span style={{ marginLeft: 'auto' }}>
              <StateChip state={focused.state} />
            </span>
          </div>

          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>{focused.statement}</p>

          <div
            style={{
              background: 'var(--sunk)',
              borderLeft: '2px solid var(--bd)',
              padding: '7px 9px',
              fontSize: 11.5,
              color: 'var(--mu)',
              lineHeight: 1.5,
            }}
          >
            {focused.evidenceExpectation === null ? (
              <>
                <b>No evidence expectation has been written for this item yet</b> — so there is
                nothing quoted to check against. Record what you checked as an attestation (ADR 0009).
              </>
            ) : (
              <>
                <i>Evidence expected:</i> {focused.evidenceExpectation}
              </>
            )}
          </div>

          {focused.scope === 'REPOSITORY' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={LABEL}>
                Per repository{focused.rollUp === null ? '' : ` · ${focused.rollUp.label}`}
              </span>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>
                Answered per repository; the item takes the worst of them. One lagging repository
                is never averaged away.
              </p>
              {focused.repoResults.map((repo) => {
                const key = repoKey(focused.ref, repo.repositoryId);
                const draft = repoGapDraft[key];
                // Open while a note is being typed, and whenever this repository is
                // already Partial — the note is part of that answer, so it should be
                // visible and editable rather than hidden behind the state that needs it.
                const noteOpen = draft !== undefined || repo.state === 'PARTIAL';

                return (
                  <div
                    key={repo.repositoryId}
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="mono" style={{ flex: 1, fontSize: 11.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {repo.pathWithNamespace}
                      </span>
                      {repo.isPrimary && <span style={{ ...LABEL, fontSize: 9.5 }}>primary</span>}
                      <select
                        // While a note is being typed the choice is Partial, even though
                        // nothing has been written yet.
                        value={draft === undefined ? repo.state : 'PARTIAL'}
                        disabled={readOnly}
                        onChange={(e) => {
                          const next = e.target.value as ItemState;

                          if (next === 'PARTIAL' && (repo.gapNote ?? '').trim() === '') {
                            // Hold the write open for its note instead of sending a
                            // Partial the API is certain to refuse.
                            setRepoGapDraft((drafts) => ({ ...drafts, [key]: '' }));
                            return;
                          }

                          setRepoGapDraft((drafts) => {
                            const { [key]: _abandoned, ...rest } = drafts;
                            return rest;
                          });
                          patchRepo.mutate({
                            ref: focused.ref,
                            repositoryId: repo.repositoryId,
                            state: next,
                          });
                        }}
                        style={{
                          height: 24,
                          border: '1px solid var(--bd)',
                          borderRadius: 'var(--r-md)',
                          background: 'var(--sf)',
                          color: 'var(--tx)',
                          font: '500 11px/1 var(--font-sans)',
                        }}
                      >
                        {(['PENDING', 'PARTIAL', 'VERIFIED', 'REJECTED', 'NOT_APPLICABLE'] as ItemState[]).map(
                          (state) => (
                            <option key={state} value={state}>{stateLabel(state)}</option>
                          ),
                        )}
                      </select>
                    </div>

                    {noteOpen && (
                      <input
                        value={draft ?? repo.gapNote ?? ''}
                        disabled={readOnly}
                        placeholder="Which part is missing on this repository?"
                        // Only when the field has just been opened by choosing Partial,
                        // so re-rendering an existing note does not steal the caret.
                        autoFocus={draft === ''}
                        onChange={(e) =>
                          setRepoGapDraft((drafts) => ({ ...drafts, [key]: e.target.value }))
                        }
                        onBlur={() => commitRepoNote(focused.ref, repo)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitRepoNote(focused.ref, repo);
                          }
                        }}
                        style={{
                          height: 26,
                          marginLeft: 10,
                          padding: '0 8px',
                          border: '1px solid var(--bd)',
                          borderRadius: 'var(--r-md)',
                          background: 'var(--sf)',
                          color: 'var(--tx)',
                          font: '400 11px/1 var(--font-sans)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={LABEL}>State</span>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {STATES.map((state) => (
                  <button
                    key={state}
                    disabled={readOnly}
                    onClick={() => setState(focused, state)}
                    title={
                      state === 'NOT_APPLICABLE' && !focused.isContextual
                        ? 'Only contextual items can be marked not applicable'
                        : state === 'WAIVED'
                          ? 'Only once a waiver for this item has two signatures. Use the link below.'
                          : undefined
                    }
                    style={{
                      border: focused.state === state ? '1.5px solid var(--ac)' : '1px solid var(--bd)',
                      borderRadius: 'var(--r-md)',
                      background: focused.state === state ? 'var(--acs)' : 'var(--sf)',
                      color: 'var(--tx)',
                      font: '600 11px/1 var(--font-sans)',
                      padding: '6px 8px',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      opacity: readOnly ? 0.5 : 1,
                    }}
                  >
                    {stateLabel(state)}
                  </button>
                ))}
              </div>
              {/*
                The route to Waived, stated at the point of need.
                
                An item cannot be set to Waived directly — it takes a waiver with two
                signatures from two roles (section 3.8), and the API refuses anything less.
                Without this link the only clue was a greyed button, which is how someone
                ends up hunting for a screen they cannot find.
              */}
              <Link
                to={`/projects/${data.audit.projectId}/waivers?item=${focused.ref}&audit=${id}`}
                style={{ fontSize: 11, color: 'var(--ac)' }}
              >
                Waived needs an approved waiver — request one for {focused.ref}
              </Link>
            </div>
          )}

          {save !== null && save.ref === focused.ref && save.status === 'failed' && (
            <div
              style={{
                background: 'var(--bab)',
                border: '1px solid var(--babd)',
                borderRadius: 'var(--r-lg)',
                padding: '9px 11px',
                fontSize: 11.5,
                color: 'var(--ba)',
                lineHeight: 1.5,
              }}
            >
              {save.message}
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {/*
              The label has to say which note this is. For a repository-scoped item this
              field writes the ITEM's note, and the per-repository notes are in the matrix
              above — reading it as "the note Partial needs" and then finding Partial
              refused on a repository is what made the two feel unrelated.
            */}
            <span style={LABEL}>
              {focused.scope === 'REPOSITORY'
                ? 'Item-level gap note — each repository has its own, above'
                : 'Gap note — required for Partial'}
            </span>
            <textarea
              value={draftNote}
              disabled={readOnly}
              onChange={(e) => setDraftNote(e.target.value)}
              onBlur={() => {
                if (draftNote !== (focused.gapNote ?? '')) {
                  patch.mutate({ ref: focused.ref, version: focused.version, gapNote: draftNote });
                }
              }}
              rows={2}
              style={{
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-lg)',
                background: 'var(--sf)',
                color: 'var(--tx)',
                font: '400 12px/1.5 var(--font-sans)',
                padding: '7px 9px',
                resize: 'vertical',
              }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={LABEL}>Attestation — instead of an artefact (ADR 0008)</span>
            <textarea
              value={draftAttestation}
              disabled={readOnly}
              onChange={(e) => setDraftAttestation(e.target.value)}
              onBlur={() => {
                if (draftAttestation !== (focused.attestation ?? '')) {
                  patch.mutate({
                    ref: focused.ref,
                    version: focused.version,
                    attestation: draftAttestation,
                  });
                }
              }}
              rows={2}
              placeholder="What you checked, and how you confirmed it."
              style={{
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-lg)',
                background: 'var(--sf)',
                color: 'var(--tx)',
                font: '400 12px/1.5 var(--font-sans)',
                padding: '7px 9px',
                resize: 'vertical',
              }}
            />
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <span style={LABEL}>Evidence · {focused.evidenceCount}</span>
            {focused.evidence.map((evidence) => (
              <div
                key={evidence.id}
                style={{
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--r-lg)',
                  padding: '8px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ ...LABEL, fontSize: 9.5 }}>{evidence.system}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{evidence.label}</span>
                  <span style={{ color: 'var(--mu)' }}><EvidenceGlyph size={12} /></span>
                </div>
                {evidence.url !== null && (
                  <a
                    href={evidence.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mono"
                    style={{ fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {evidence.url}
                  </a>
                )}
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: 'var(--mu)' }}>
                  <Avatar initials={evidence.capturedBy.initials} size={16} />
                  {evidence.capturedBy.displayName} · {new Date(evidence.capturedAt).toLocaleDateString('en-GB')}
                </span>
              </div>
            ))}

            {!readOnly && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <input
                  value={evidenceLabel}
                  onChange={(e) => setEvidenceLabel(e.target.value)}
                  placeholder="What it is"
                  style={{
                    height: 28,
                    border: '1px solid var(--bd)',
                    borderRadius: 'var(--r-md)',
                    background: 'var(--sf)',
                    color: 'var(--tx)',
                    font: '400 12px/1 var(--font-sans)',
                    padding: '0 8px',
                  }}
                />
                <input
                  ref={evidenceUrlRef}
                  value={evidenceUrl}
                  onChange={(e) => setEvidenceUrl(e.target.value)}
                  placeholder="https://…"
                  style={{
                    height: 28,
                    border: '1px solid var(--bd)',
                    borderRadius: 'var(--r-md)',
                    background: 'var(--sf)',
                    color: 'var(--tx)',
                    font: '400 12px/1 var(--font-sans)',
                    padding: '0 8px',
                  }}
                />
                <PermissionButton
                  onClick={() =>
                    addEvidence.mutate({
                      ref: focused.ref,
                      label: evidenceLabel,
                      url: evidenceUrl,
                      system: 'GITLAB',
                    })
                  }
                  disabledReason={
                    evidenceLabel.trim().length < 2
                      ? 'Say what the evidence is.'
                      : !evidenceUrl.startsWith('http')
                        ? 'A retrievable link, so somebody else can check it.'
                        : null
                  }
                >
                  Attach evidence
                </PermissionButton>
              </div>
            )}
          </div>

          {focused.recordedValueFields.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={LABEL}>Measured values</span>
              {focused.recordedValueFields.map((field) => (
                <span key={field.label} style={{ fontSize: 11.5, color: 'var(--mu)' }}>
                  {field.label} ({field.unit}) —{' '}
                  {focused.recordedValues.find((value) => value.label === field.label)?.value ??
                    'not recorded'}
                </span>
              ))}
            </div>
          )}
        </aside>
      )}
    </div>
  );
}
